import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AlertCustomComponent } from 'pharedata-ng-components';
import { ParentMessagesSender } from '../../services/parent-messages-sender';
import { LlamanosApiService } from '../../services/api-llamanos.service';
import { GlobalState } from '../../services/global-state';
import { DatePickerModule } from 'primeng/datepicker';
import { getChileTimeZone } from '../../utils/timeZone';
import {
  getFieldKey,
  formatLabel,
  isAllowedEvidenceFile,
  ALLOWED_EVIDENCE_ACCEPT,
  ALLOWED_EVIDENCE_FORMATS_LABEL,
  uploadEvidenceWithPresignedUrl,
} from '../../utils/utils';
import { ES_DATE_LOCALE } from '../../utils/timeZone';

@Component({
  selector: 'app-formulario',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    AlertCustomComponent,
    DatePickerModule,
  ],
  templateUrl: 'formulario.component.html',
  styleUrls: ['formulario.component.scss'],
})
export class FormularioComponent implements OnChanges {
  @Input() subcategoria: any;
  @Input() categoria: string = '';
  @Input() categoriaType: string = '';
  @Input() selectedEstacion: {
    label: string;
    value: number;
    desde: Date;
  } | null = null;
  @Output() closeFormulario = new EventEmitter<void>();
  @Output() finish = new EventEmitter<string>();

  es = ES_DATE_LOCALE;

  hasError: boolean = false;
  descripcionCaso: string = '';
  isPresubmit: boolean = false;
  showAlert: boolean = false;
  alertTitle = '';
  alertContent = '';
  alertIcon = '';
  numeroCaso: string = '';
  heridos: 'Si' | 'No' | null = null;
  userValues: Record<string, string> = {
    nombres: '',
    apellidos: '',
    email: '',
    telefono: '',
  };
  selectedEvidenceFile: File | null = null;
  selectedEvidenceName: string = '';
  protected readonly allowedEvidenceAccept = ALLOWED_EVIDENCE_ACCEPT;
  protected readonly allowedEvidenceFormatsLabel =
    ALLOWED_EVIDENCE_FORMATS_LABEL;

  formValues: Record<string, any> = {};
  displayFields: any[] = [];
  canSubmit: boolean = false;
  private allFields: any[] = []; // Guardar todos los campos para buscar campos hijos

  halfWidthFields: Array<{ displayLabel: string; key: string }> = [
    { displayLabel: 'NOMBRES', key: 'nombres' },
    { displayLabel: 'APELLIDOS', key: 'apellidos' },
    { displayLabel: 'EMAIL', key: 'email' },
    { displayLabel: 'TELÉFONO', key: 'telefono' },
  ];

  private api = inject(LlamanosApiService);
  private parentMessagesSender = inject(ParentMessagesSender);
  private globalState = inject(GlobalState);
  /*
  constructor(
    private globalState: GlobalState,
    private parentMessagesSender: ParentMessagesSender,
    private dialog: MatDialog,
    private apiLlamanosService: LlamanosApiService
  ) {}
*/
  getFieldKey = getFieldKey;

  onCancel(): void {
    this.closeFormulario.emit();
  }

  onHeridosChange(value: any): void {
    this.heridos = value === 'Si' || value === true ? 'Si' : 'No';
    this.formValues['heridos'] = this.heridos;
    this.updateCanSubmit();
  }

  onPicklistChange(field: any, value: any): void {
    const fieldKey = getFieldKey(field);
    this.formValues[fieldKey] = value;
    // Actualizar campos visibles cuando cambia un picklist
    this.updateDisplayFields();
    this.updateCanSubmit();
  }

  formatLabel = formatLabel;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subcategoria']) {
      const fields = (this.subcategoria?.fields ?? []) as any[];
      // Guardar todos los campos para poder buscar campos hijos
      this.allFields = fields;
      // Actualizar campos visibles
      this.updateDisplayFields();
    }
    this.updateCanSubmit();
  }

  private updateDisplayFields(): void {
    const fields = this.allFields;
    // Filtrar campos base que no tengan campo_padre o que campo_padre sea null o ""
    const baseFields = fields.filter((f) => {
      const campoPadre = f?.campo_padre;
      return (
        campoPadre === null || campoPadre === undefined || campoPadre === ''
      );
    });

    // Campos base procesados
    const processedFields = baseFields
      .filter(
        (f) =>
          f?.field_name !== 'ArmasHeridos__c' &&
          f.field_name !== 'EstacionDeServicio__c',
      )
      .map((f) => ({
        ...f,
        displayLabel: formatLabel(f?.label ?? ''),
        required: this.subcategoria?.compulsory_fields?.includes(f?.field_name),
      }));

    // Obtener los field_name de los campos dependientes que actualmente están visibles
    const currentDependentFieldNames = new Set<string>();
    for (const field of this.displayFields) {
      // Buscar si este campo es un subcampo de algún picklist
      const isDependent = baseFields.some(
        (bf) =>
          bf?.field_type === 'picklist' && bf?.subcampo === field?.field_name,
      );
      if (isDependent) {
        currentDependentFieldNames.add(field.field_name);
      }
    }

    // Buscar campos hijos que deben mostrarse basándose en valores seleccionados en picklists
    const dependentFields: any[] = [];
    const visibleDependentFieldNames = new Set<string>();

    for (const field of baseFields) {
      if (
        field?.field_type === 'picklist' &&
        field?.desencadenante &&
        field?.subcampo
      ) {
        const fieldKey = getFieldKey(field);
        const selectedValue = this.formValues[fieldKey];

        if (selectedValue) {
          // Verificar si el valor seleccionado está en desencadenante
          const desencadenante = Array.isArray(field.desencadenante)
            ? field.desencadenante
            : [field.desencadenante];

          if (desencadenante.includes(selectedValue)) {
            // Buscar el campo hijo con field_name igual a subcampo
            const childField = fields.find(
              (f) => f?.field_name === field.subcampo,
            );
            if (childField) {
              visibleDependentFieldNames.add(childField.field_name);
              // Verificar si ya está en dependentFields para evitar duplicados
              if (
                !dependentFields.some(
                  (f) => f?.field_name === childField.field_name,
                )
              ) {
                dependentFields.push({
                  ...childField,
                  displayLabel: formatLabel(childField?.label ?? ''),
                  required: this.subcategoria?.compulsory_fields?.includes(
                    childField?.field_name,
                  ),
                });
              }
            }
          }
        }
      }
    }

    // Limpiar valores de campos dependientes que ya no están visibles
    for (const fieldName of currentDependentFieldNames) {
      if (!visibleDependentFieldNames.has(fieldName)) {
        // Limpiar el valor del campo dependiente que ya no es visible
        const field = fields.find((f) => f?.field_name === fieldName);
        if (field) {
          const fieldKey = getFieldKey(field);
          delete this.formValues[fieldKey];
        }
      }
    }

    // Combinar campos base con campos dependientes y ordenar por sort
    const allFields = [...processedFields, ...dependentFields];
    // Ordenar por sort ascendente (si no viene, asumir 0)
    allFields.sort((a: any, b: any) => {
      const sa = typeof a?.sort === 'number' ? a.sort : 0;
      const sb = typeof b?.sort === 'number' ? b.sort : 0;
      return sa - sb;
    });
    this.displayFields = allFields;
  }

  validateNumber(field: any): void {
    const key = getFieldKey(field);
    let value = String(this.formValues[key] || '');

    const scale = parseInt(String(field?.scale || '0'), 10);
    const precision = parseInt(String(field?.precision || '1'), 10);

    // Si scale es 0, eliminar todo lo que esté después del primer punto o coma
    if (scale === 0) {
      const match = value.match(/^([^.,]*)/);
      value = match ? match[1] : value;
    } else {
      // Si scale > 0, limitar decimales
      const parts = value.split(/[.,]/);
      if (parts.length > 1) {
        const integerPart = parts[0] || '';
        const decimalPart = parts[1] || '';
        const limitedDecimal = decimalPart.substring(0, scale);
        value = integerPart + (limitedDecimal ? '.' + limitedDecimal : '');
      }
    }

    // Limitar longitud total según precision
    // Remover el punto decimal del conteo si existe
    const valueWithoutDecimal = value.replace(/[.,]/g, '');
    if (valueWithoutDecimal.length > precision) {
      if (scale === 0) {
        value = valueWithoutDecimal.substring(0, precision);
      } else {
        const parts = value.split(/[.,]/);
        const integerPart = parts[0] || '';
        const decimalPart = parts[1] || '';
        const maxIntegerLength = precision - scale;
        const limitedInteger = integerPart.substring(0, maxIntegerLength);
        const limitedDecimal = decimalPart.substring(0, scale);
        value = limitedInteger + (limitedDecimal ? '.' + limitedDecimal : '');
      }
    }

    this.formValues[key] = value;
    this.updateCanSubmit();
  }

  onNumberKeyDown(event: KeyboardEvent, field: any): void {
    const scale = parseInt(String(field?.scale || '0'), 10);
    const precision = parseInt(String(field?.precision || '18'), 10);
    const input = event.target as HTMLInputElement;
    const currentValue = String(input.value || '');
    const key = event.key;

    // Permitir teclas de control
    if (
      event.ctrlKey ||
      event.metaKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(key)
    ) {
      return;
    }

    // Si scale es 0, no permitir punto ni coma
    if (scale === 0 && (key === '.' || key === ',')) {
      event.preventDefault();
      return;
    }

    // Permitir solo números, punto o coma si scale > 0
    if (!/^[0-9.,]$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Si es punto o coma y scale > 0
    if ((key === '.' || key === ',') && scale > 0) {
      // No permitir si ya hay un punto o coma
      if (currentValue.includes('.') || currentValue.includes(',')) {
        event.preventDefault();
        return;
      }
    }

    // Validar longitud máxima
    const valueWithoutDecimal = currentValue.replace(/[.,]/g, '');
    if (valueWithoutDecimal.length >= precision) {
      // Si scale > 0 y no hay punto/coma, permitir agregar punto/coma
      if (
        scale > 0 &&
        !currentValue.includes('.') &&
        !currentValue.includes(',') &&
        (key === '.' || key === ',')
      ) {
        return;
      }
      // Si ya hay punto/coma y estamos agregando decimales, verificar límite de decimales
      if (currentValue.includes('.') || currentValue.includes(',')) {
        const parts = currentValue.split(/[.,]/);
        const decimalPart = parts[1] || '';
        if (decimalPart.length >= scale) {
          event.preventDefault();
          return;
        }
      } else {
        // No permitir más dígitos enteros
        event.preventDefault();
        return;
      }
    }

    // Si ya hay punto/coma y scale > 0, verificar límite de decimales
    if (
      (currentValue.includes('.') || currentValue.includes(',')) &&
      scale > 0
    ) {
      const parts = currentValue.split(/[.,]/);
      const decimalPart = parts[1] || '';
      if (decimalPart.length >= scale && /^[0-9]$/.test(key)) {
        event.preventDefault();
        return;
      }
    }
  }

  onPhoneKeyDown(event: KeyboardEvent, field: any): void {
    const input = event.target as HTMLInputElement;
    const currentValue = String(input.value || '');
    const key = event.key;

    // Permitir teclas de control
    if (
      event.ctrlKey ||
      event.metaKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(key)
    ) {
      return;
    }

    // Permitir solo números del 0 al 9
    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Validar longitud máxima de 8 caracteres
    if (currentValue.length >= 8) {
      event.preventDefault();
      return;
    }
  }

  private isEmptyValue(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }

  updateCanSubmit(): void {
    const hasDescripcion =
      typeof this.descripcionCaso === 'string' &&
      this.descripcionCaso.trim().length > 0;

    const requiredFields = (this.displayFields || []).filter(
      (f) => !!f?.required,
    );
    for (const field of requiredFields) {
      const key = getFieldKey(field);
      const value = this.formValues[key];
      if (this.isEmptyValue(value)) {
        this.canSubmit = false;
        return;
      }
    }

    for (const key in this.userValues) {
      if (this.isEmptyValue(this.userValues[key])) {
        this.canSubmit = false;
        return;
      }
    }

    this.canSubmit = hasDescripcion;
  }

  onAttachEvidenceClick(input: HTMLInputElement): void {
    input.click();
  }

  onEvidenceSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files.length > 0 ? input.files[0] : null;
    if (!file) return;

    const maxBytes = 10 * 1024 * 1024; // 10MB
    const name = file.name || '';

    if (file.size > maxBytes) {
      this.alertTitle = 'Archivo demasiado grande';
      this.alertContent = 'El máximo permitido es 10MB.';
      this.alertIcon = 'error';
      this.showAlert = true;
      this.selectedEvidenceFile = null;
      this.selectedEvidenceName = '';
      (event.target as HTMLInputElement).value = '';
      this.hasError = true;
      return;
    }

    if (!isAllowedEvidenceFile(file)) {
      this.alertTitle = 'Archivo no permitido';
      this.alertContent = `Solo se permiten archivos con formato ${this.allowedEvidenceFormatsLabel}.`;
      this.alertIcon = 'error';
      this.showAlert = true;
      this.selectedEvidenceFile = null;
      this.selectedEvidenceName = '';
      input.value = '';
      this.hasError = true;
      return;
    }

    this.selectedEvidenceFile = file;
    this.selectedEvidenceName = name;
  }

  preSubmit(): void {
    const nameRegex = /^[A-Za-zÁÉÍÓÚáéíóúÑñ' ]+$/;
    const emailRegex = /^[\w-.]+@[\w-]+\.[A-Za-z]{2,}$/;
    const phoneRegex = /^[0-9]{8}$/;

    const nombres = this.userValues['nombres']?.trim() || '';
    const apellidos = this.userValues['apellidos']?.trim() || '';
    const email = this.userValues['email']?.trim() || '';
    const telefono = this.userValues['telefono']?.trim() || '';
    let validationError = false;

    if (this.categoriaType === 'Siniestro' && !this.heridos) {
      this.alertContent = 'Por favor ingresa si hay heridos y/o armas.';
      validationError = true;
    }

    if (!nameRegex.test(nombres)) {
      this.alertContent = 'Por favor ingresa un nombre válido.';
      validationError = true;
    }

    if (!nameRegex.test(apellidos)) {
      this.alertContent = 'Por favor ingresa apellidos válidos.';
      validationError = true;
    }

    if (!emailRegex.test(email)) {
      this.alertContent = 'Por favor ingresa un email válido.';
      validationError = true;
    }

    if (!phoneRegex.test(telefono)) {
      this.alertContent = 'Por favor ingresa un teléfono válido.';
      validationError = true;
    }

    if (
      this.selectedEvidenceFile &&
      !isAllowedEvidenceFile(this.selectedEvidenceFile)
    ) {
      this.alertTitle = 'Archivo no permitido';
      this.alertContent = `Solo se permiten archivos con formato ${this.allowedEvidenceFormatsLabel}.`;
      this.alertIcon = 'error';
      this.showAlert = true;
      this.hasError = true;
      return;
    }

    if (validationError) {
      this.alertTitle = 'Error datos ingresados';
      this.alertIcon = 'error';
      this.showAlert = true;
      this.hasError = true;
      return;
    }

    if (!this.validateFields()) {
      return;
    }

    this.isPresubmit = true;
    this.alertTitle = '¡Importante!';
    this.alertContent =
      'Considere que al "Ingresar Llámanos" este se enviará sin posibilidad de editarlo nuevamente. ¿Está seguro de continuar?';
    this.alertIcon = 'important';
    this.showAlert = true;
  }

  private validateFields(): boolean {
    const emailRegex = /^[\w-.]+@[\w-]+\.[A-Za-z]{2,}$/;

    for (const field of this.displayFields) {
      if (field?.field_type === 'email') {
        const key = getFieldKey(field);
        const value = String(this.formValues[key] || '').trim();

        // Si el campo no está vacío, debe ser un email válido
        if (value !== '' && !emailRegex.test(value)) {
          this.alertTitle = 'Error datos ingresados';
          this.alertContent = `Ingresa un email válido en el campo ${field?.displayLabel || field?.field_name}.`;
          this.alertIcon = 'error';
          this.showAlert = true;
          this.hasError = true;
          return false;
        }
      }

      if (field?.field_type === 'phone') {
        const key = getFieldKey(field);
        const value = String(this.formValues[key] || '').trim();

        // El campo debe tener exactamente 8 caracteres
        if (value.length !== 8 && value.length !== 0) {
          this.alertTitle = 'Error datos ingresados';
          this.alertContent = `Ingresa un teléfono válido en el campo ${field?.displayLabel || field?.field_name}`;
          this.alertIcon = 'error';
          this.showAlert = true;
          this.hasError = true;
          return false;
        }
      }
    }

    return true;
  }

  async onSubmit(): Promise<void> {
    // Si hay archivo adjunto, primero obtener la URL prefirmada, subir el archivo
    // y luego usar la key devuelta al crear el caso.
    let presignedKey: string | null = null;
    if (this.selectedEvidenceFile) {
      const nombreArchivo =
        this.selectedEvidenceName ||
        this.selectedEvidenceFile.name ||
        'archivo';
      try {
        presignedKey = await uploadEvidenceWithPresignedUrl({
          file: this.selectedEvidenceFile,
          fileName: nombreArchivo,
          getPresignedUrl: (n, t) => this.api.getPresignedUrl(n, t),
          uploadFileToPresignedUrl: (u, f) =>
            this.api.uploadFileToPresignedUrl(u, f),
        });
      } catch (err) {
        this.alertTitle = 'Error';
        this.alertContent =
          err instanceof Error
            ? err.message
            : 'Ha ocurrido un error subiendo el archivo adjunto.';
        this.alertIcon = 'error';
        this.showAlert = true;
        this.hasError = true;
        return;
      }
    }

    const body: any = {};
    const parametros = [];
    if (this.categoriaType?.toLowerCase() === 'siniestro') {
      body['heridos'] = this.heridos;
      parametros.push({ campo: 'ArmasHeridos__c', contenido: this.heridos });
    }
    for (const field of this.displayFields) {
      const key = getFieldKey(field);
      let contenido = this.formValues[key];

      if (
        (contenido !== 0 && contenido !== false && !contenido) ||
        contenido.length == 0
      )
        continue;

      if (field.field_type == 'phone') contenido = '+569' + contenido;

      if (field.field_type == 'datetime') {
        const timeZone = getChileTimeZone();
        contenido =
          contenido.toLocaleString('sv-SE').replace(' ', 'T') + timeZone;
      }

      if (field.field_type == 'multipicklist') contenido = contenido.join(';');

      parametros.push({
        campo: field?.field_name,
        contenido: contenido,
      });
    }
    body['eds'] = this.selectedEstacion?.value;
    body['categoria'] = this.categoria;
    body['subcategoria'] = this.subcategoria?.key;
    body['parametros'] = parametros;
    body['descripcion'] = this.descripcionCaso;
    body['cliente'] = this.userValues;

    if (this.selectedEvidenceFile && presignedKey) {
      const filename =
        this.selectedEvidenceName ||
        this.selectedEvidenceFile.name ||
        'archivo';
      const dotIndex = filename.lastIndexOf('.');
      const titulo = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
      body['archivo'] = {
        nombre: filename,
        titulo,
        s3Key: presignedKey,
        key: presignedKey,
      };
    }

    this.api.crearCaso(body).subscribe({
      next: (res: any) => {
        this.numeroCaso = res.case?.numero_caso || '';
        this.alertTitle = '¡Operación exitosa!';
        this.alertContent =
          'El Llámanos ha sido ingresado con éxito. N° de Caso: ' +
          this.numeroCaso;
        this.alertIcon = 'success';
        this.showAlert = true;
      },
      error: (err: unknown) => {
        if (typeof err === 'object' && err !== null && 'error' in err) {
          const e = err as { error?: any };
          this.alertContent =
            'Ha ocurrido un error creando el caso. ' +
            (e.error?.case?.error ?? '');
        } else {
          this.alertContent = 'Ha ocurrido un error creando el caso.';
        }

        this.numeroCaso = '';
        this.alertTitle = 'Error';
        this.alertIcon = 'error';
        this.showAlert = true;
        this.hasError = true;
      },
    });
  }

  removeFile = () => {
    this.selectedEvidenceFile = null;
    this.selectedEvidenceName = '';
  };

  acceptAlert = () => {
    this.hideGreyBackground();
    this.showAlert = false;
    if (this.isPresubmit) {
      this.isPresubmit = false;
      this.onSubmit();
    } else if (this.hasError) {
      this.hasError = false;
    } else {
      this.finish.emit(this.numeroCaso);
    }
  };

  closeAlert = () => {
    this.showAlert = false;
    if (this.isPresubmit) this.isPresubmit = false;
    else if (this.hasError) {
      this.hasError = false;
    } else this.finish.emit(this.numeroCaso);
  };

  hideGreyBackground = () => {
    this.parentMessagesSender.hideGreyBackground();
  };

  showGreyBackground = () => {
    this.parentMessagesSender.showGreyBackground();
  };

  scrollTo = (x: number, y: number) => {
    this.parentMessagesSender.scrollTo(x, y);
  };
}
